import { useEffect, useState, useCallback } from 'react';
import { useStore } from './store';
import { useWebSocket } from './hooks/useWebSocket';
import { AuthPage } from './components/AuthPage';
import { OnboardingPage } from './components/OnboardingPage';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';

/** Authenticated shell — connects WS and shows Layout once connected */
function AuthenticatedApp() {
  const connected = useStore((s) => s.connected);
  const ws = useWebSocket();

  if (!connected) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100vw', height: '100vh', background: '#1b1b14', color: '#9E9E8E', fontSize: 14 }}>
        连接中...
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Layout send={ws.send} sendBinary={ws.sendBinary} />
    </ErrorBoundary>
  );
}

export function App() {
  const token = useStore((s) => s.token);
  const logout = useStore((s) => s.logout);
  const [needsOnboarding, setNeedsOnboarding] = useState(
    () => localStorage.getItem('novax:needsOnboarding') === 'true',
  );

  const completeOnboarding = useCallback(() => {
    localStorage.removeItem('novax:needsOnboarding');
    setNeedsOnboarding(false);
  }, []);

  // Sync onboarding flag when token changes (login triggers re-render but useState initializer doesn't re-run)
  useEffect(() => {
    if (token && localStorage.getItem('novax:needsOnboarding') === 'true') {
      setNeedsOnboarding(true);
    }
  }, [token]);

  // Validate token on mount — if expired/invalid, auto-logout
  useEffect(() => {
    if (!token) return;
    fetch('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      if (!r.ok) logout();
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // If URL has ?invite= or ?verify=, show AuthPage even if logged in
  const hasAuthParam = typeof window !== 'undefined' &&
    (window.location.search.includes('invite=') || window.location.search.includes('verify='));

  if (!token || hasAuthParam) {
    return <AuthPage />;
  }

  if (needsOnboarding) {
    return <OnboardingPage onComplete={completeOnboarding} />;
  }

  return <AuthenticatedApp />;
}
